import React, { useState } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import sanitizeHtml from 'sanitize-html';
import cn from '../utils/cn';
import KoobLoader from './KoobLoader';

const accordionVariants = cva('w-full overflow-hidden rounded-lg', {
  variants: {
    variant: {
      default: 'border border-solid border-gray-200 bg-white',
      muted: 'border border-solid border-transparent bg-gray-100',
    },
  },
  defaultVariants: { variant: 'default' },
});

// Legacy content is HTML built app-side (policies, fees, media links) — keep
// the classes/targets those strings rely on, strip everything dangerous.
const sanitizeOptions: sanitizeHtml.IOptions = {
  allowedAttributes: {
    ...sanitizeHtml.defaults.allowedAttributes,
    '*': ['class'],
    a: ['href', 'target', 'rel'],
  },
};

type KoobAccordionGroupProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'className'
>;

export const KoobAccordionGroup = React.forwardRef<
  HTMLDivElement,
  KoobAccordionGroupProps
>(({ children, ...props }, ref) => (
  <div ref={ref} className="flex w-full flex-col gap-2" {...props}>
    {children}
  </div>
));

KoobAccordionGroup.displayName = 'KoobAccordionGroup';

type KoobAccordionProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'className' | 'title'
> &
  VariantProps<typeof accordionVariants> & {
    title: React.ReactNode;
    /** Start expanded (still toggleable) */
    defaultOpen?: boolean;
    /** Always expanded, header is not clickable and has no chevron */
    alwaysOpen?: boolean;
    /** Spinner instead of content while open */
    loading?: boolean;
    'data-testid'?: string;
  };

const KoobAccordion = React.forwardRef<HTMLDivElement, KoobAccordionProps>(
  (
    {
      title,
      variant,
      defaultOpen = false,
      alwaysOpen = false,
      loading = false,
      children,
      'data-testid': dataCy,
      ...props
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const open = alwaysOpen || isOpen;

    return (
      <div ref={ref} className={accordionVariants({ variant })} {...props}>
        <button
          type="button"
          onClick={alwaysOpen ? undefined : () => setIsOpen(!isOpen)}
          aria-expanded={open}
          data-testid={dataCy}
          className={cn(
            'flex w-full items-center justify-between gap-2 border-none bg-transparent p-4 text-left',
            !alwaysOpen && 'cursor-pointer',
          )}
        >
          <span className="text-sm font-semibold text-gray-900">{title}</span>
          {!alwaysOpen && (
            <svg
              className={cn(
                'h-5 w-5 shrink-0 text-gray-400 transition-transform duration-200',
                open && 'rotate-180',
              )}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          )}
        </button>

        {open && (
          <div className="border-0 border-t border-solid border-gray-200 p-4 text-sm text-gray-700">
            {loading ? (
              <div className="flex justify-center py-2">
                <KoobLoader size={24} />
              </div>
            ) : typeof children === 'string' ? (
              <div
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(children, sanitizeOptions),
                }}
              />
            ) : (
              children
            )}
          </div>
        )}
      </div>
    );
  },
);

KoobAccordion.displayName = 'KoobAccordion';

export { KoobAccordion };
